import { genEntityId, UserId, WorkflowActionId } from "@sp-crm/core";
import { RegionVisibility } from "components/ui/region-visibility-tree";
import {
    AdvancedSearchConditionNodeType,
    AdvancedSearchEntityType,
    AdvancedSearchRelativeDateInterval,
    AdvancedSearchRequestCondition,
    FieldConditionOperator,
    GetCustomListQuery,
    WorkflowActionEmailEntityInput,
    WorkflowActionEntityInput,
    WorkflowActionSmsEntityInput,
    WorkflowActionTaskEntityInput,
    WorkflowActorMode,
    WorkflowEventType,
    WorkflowPayload,
    WorkflowPayloadSettingsInput,
    WorkflowSettingType,
    WorkflowStatus,
    WorkflowTriggerInput,
} from "generated/graphql";
import { CrmMode } from "util/crm-mode";
import { EMAIL_OPENED_WAIT_RECIPE } from "../manage-workflows-helper";
import {
    actionEntityDelayRecipe,
    actionEntityId,
    actionEntitySettingSuffix,
    actionSettingKey,
    isActionIntervalRecipe,
    isWorkflowWaitRecipe,
    nextActionSettingSuffix,
    withActionEntityDelayRecipe,
    workflowEntityTypeLabel,
} from "./builder-helper";

export type WorkflowTriggerKind =
    | "statusChange"
    | "checkLater"
    | "moveIn"
    | "referralSourceChosen"
    | "communityChosen"
    | "emailOpened"
    | "inactivity"
    | "careStart"
    | "addedToOrganization";

export type EmailAudience = "recipients" | "unselectedCommunities";

export type WorkflowAction =
    | { kind: "task" }
    | { kind: "email"; audience: EmailAudience }
    | { kind: "sms" };

export type WorkflowActionKind = WorkflowAction["kind"];

export interface BlueprintAction {
    id: WorkflowActionId;
    action: WorkflowAction;
}

export interface WorkflowBlueprint {
    entityType: AdvancedSearchEntityType;
    triggerKind: WorkflowTriggerKind;
    actions: BlueprintAction[];
}

export type ActionMoveDirection = "up" | "down";

type CustomList = GetCustomListQuery["getCustomList"];

export interface BlueprintContext {
    clientStatusCustomList: CustomList | null;
    homeCareLeadStatusCustomList: CustomList | null;
    newActionId: () => WorkflowActionId;
}

export interface TriggerOption {
    kind: WorkflowTriggerKind;
    title: string;
    description: string;
}

export interface ActionOption {
    key: string;
    action: WorkflowAction;
    title: string;
    description: string;
}

export interface StoredSetting {
    key: string;
    textValue: string;
}

export interface BlueprintResult {
    payload: WorkflowPayload;
    settingResets: StoredSetting[];
}

export interface NewWorkflowInput {
    currentUserId: UserId;
    regionVisibility: RegionVisibility[];
    existingNames: string[];
}

const DEFAULT_WORKFLOW_NAME = "My Workflow";
const EMAIL_WAIT_RECIPE = "10m";
const TASK_WAIT_RECIPE = "1m";
const COMMUNITY_BROADCAST_WAIT_RECIPE = "1m";
export const DEFAULT_ACTION_INTERVAL_RECIPE = "3d";

export const generateActionId = (): WorkflowActionId => genEntityId<WorkflowActionId>();
const DEFAULT_DUE_DATE_DAYS = "1";
const STATUS_CHANGE_DUE_DATE_DAYS = "7";
const INACTIVITY_CRON = "0 12 * * *";
const INACTIVITY_DAYS = 30;
const EMAIL_OPENED_THRESHOLD_DAYS = 21;
const PLACED_STATUS_KEYS = ["scheduled", "moved", "invoiced", "paid"];
const CARE_START_STATUS_KEYS = ["hcStartCare", "hcClosedWon"];

type StatusListKey = "client" | "homeCareLead" | null;

interface EntityProfile {
    entityType: AdvancedSearchEntityType;
    tokenRoot: string;
    noun: string;
    nameToken: string;
    defaultRecipients: string;
    referralRecipients: string | null;
    defaultSmsRecipients: string;
    referralSmsRecipients: string | null;
    statusFieldName: string;
    statusListKey: StatusListKey;
    activeCategoryKeys: string[];
    triggerKinds: WorkflowTriggerKind[];
}

const ENTITY_PROFILES: Partial<Record<AdvancedSearchEntityType, EntityProfile>> = {
    [AdvancedSearchEntityType.Client]: {
        entityType: AdvancedSearchEntityType.Client,
        tokenRoot: "client",
        noun: "client",
        nameToken: "{{client.name}}",
        defaultRecipients: "{{client.bestContactEntity.email1}},{{client.email}}",
        referralRecipients: "{{client.referralContact.contactEntity.email1}}",
        defaultSmsRecipients:
            "{{client.bestContactEntity.cellPhone}},{{client.cellPhone}}",
        referralSmsRecipients: "{{client.referralContact.contactEntity.cellPhone}}",
        statusFieldName: "statusListItem",
        statusListKey: "client",
        activeCategoryKeys: ["Active", "Ongoing"],
        triggerKinds: [
            "statusChange",
            "checkLater",
            "moveIn",
            "referralSourceChosen",
            "communityChosen",
            "emailOpened",
            "inactivity",
        ],
    },
    [AdvancedSearchEntityType.HomeCareLead]: {
        entityType: AdvancedSearchEntityType.HomeCareLead,
        tokenRoot: "homeCareLead",
        noun: "home care client",
        nameToken: "{{homeCareLead.selfContactEntity.name}}",
        defaultRecipients:
            "{{homeCareLead.selfContactEntity.email1}},{{homeCareLead.bestContactEntity.email1}}",
        referralRecipients: "{{homeCareLead.referralContact.contactEntity.email1}}",
        defaultSmsRecipients:
            "{{homeCareLead.selfContactEntity.cellPhone}},{{homeCareLead.bestContactEntity.cellPhone}}",
        referralSmsRecipients: "{{homeCareLead.referralContact.contactEntity.cellPhone}}",
        statusFieldName: "statusListItem",
        statusListKey: "homeCareLead",
        activeCategoryKeys: ["Active"],
        triggerKinds: [
            "statusChange",
            "careStart",
            "referralSourceChosen",
            "emailOpened",
            "inactivity",
        ],
    },
    [AdvancedSearchEntityType.ReferenceContact]: {
        entityType: AdvancedSearchEntityType.ReferenceContact,
        tokenRoot: "referenceContact",
        noun: "referral contact",
        nameToken: "{{referenceContact.contactEntity.name}}",
        defaultRecipients: "{{referenceContact.contactEntity.email1}}",
        referralRecipients: null,
        defaultSmsRecipients: "{{referenceContact.contactEntity.cellPhone}}",
        referralSmsRecipients: null,
        statusFieldName: "statusListItem",
        statusListKey: null,
        activeCategoryKeys: [],
        triggerKinds: ["addedToOrganization"],
    },
};

type TriggerTiming =
    | { mode: "wait"; wait: (action: WorkflowAction) => string }
    | { mode: "anchored"; dateField: string; anchorLabel: string; defaultDays: string }
    | { mode: "none"; recipe: string | null };

type StatusFilterMode = "matcher" | "emailOpened" | "schedule" | "seeded" | "none";

interface TriggerSpec {
    title: string;
    describe: (profile: EntityProfile) => string;
    stopReason: (profile: EntityProfile) => string | null;
    automatedNotificationLabel: ((profile: EntityProfile) => string) | null;
    taskDescription: (profile: EntityProfile) => string;
    dueDateDaysDefault: string;
    recipients: (profile: EntityProfile) => string;
    smsRecipients: (profile: EntityProfile) => string;
    audiences: EmailAudience[];
    timing: TriggerTiming;
    statusFilter: StatusFilterMode;
    build: (
        profile: EntityProfile,
        context: BlueprintContext,
    ) => WorkflowTriggerInput | null;
    matches: (triggerInput: WorkflowTriggerInput) => boolean;
}

const capitalize = (value: string): string =>
    value.charAt(0).toUpperCase() + value.slice(1);

const defaultWaitForAction = (action: WorkflowAction): string =>
    action.kind === "task" ? TASK_WAIT_RECIPE : EMAIL_WAIT_RECIPE;

const fieldMatcher = (fieldName: string, specialValue: string) => ({
    fieldName,
    current: { specialValue },
});

const statusMatcher = (fieldName: string, values: string[]) => ({
    fieldName,
    current: { values },
});

const entityUpdatedTrigger = (
    fieldMatchers: WorkflowTriggerInput["entityUpdated"]["fieldMatchers"],
): WorkflowTriggerInput => ({
    eventType: WorkflowEventType.EntityUpdated,
    entityUpdated: { fieldMatchers },
});

const findMatcher = (triggerInput: WorkflowTriggerInput, fieldName: string) =>
    triggerInput.entityUpdated?.fieldMatchers?.find(m => m.fieldName === fieldName) ??
    null;

const hasEntityUpdate = (
    triggerInput: WorkflowTriggerInput,
    fieldName: string,
): boolean =>
    triggerInput.eventType === WorkflowEventType.EntityUpdated &&
    !!findMatcher(triggerInput, fieldName);

const findStatusMatcher = (triggerInput: WorkflowTriggerInput) =>
    triggerInput.entityUpdated?.fieldMatchers?.find(
        matcher =>
            (matcher.fieldName === "status" || matcher.fieldName === "statusListItem") &&
            Array.isArray(matcher.current?.values) &&
            !matcher.previous?.values &&
            !matcher.previous?.specialValue,
    ) ?? null;

const findEmailOpenedStatusMatcher = (triggerInput: WorkflowTriggerInput) =>
    triggerInput.emailOpened?.entityFieldMatchers?.find(
        matcher => matcher.fieldName === "statusListItem",
    ) ?? null;

const findScheduleStatusCondition = (
    triggerInput: WorkflowTriggerInput,
): AdvancedSearchRequestCondition | null => {
    const condition = triggerInput.schedule?.entityCondition;
    if (!condition || condition.nodeType !== AdvancedSearchConditionNodeType.And) {
        return null;
    }
    return (
        (condition.children ?? []).find(
            child =>
                child.nodeType === AdvancedSearchConditionNodeType.Or &&
                (child.children ?? []).every(
                    grandchild =>
                        grandchild.nodeType ===
                            AdvancedSearchConditionNodeType.FieldCondition &&
                        grandchild.fieldName === "statusListItem",
                ),
        ) ?? null
    );
};

const findScheduleDateCondition = (triggerInput: WorkflowTriggerInput) =>
    (triggerInput.schedule?.entityCondition?.children ?? []).find(
        child =>
            child.nodeType === AdvancedSearchConditionNodeType.FieldCondition &&
            child.fieldName === "updatedAt" &&
            typeof child.relativeDate?.value === "number",
    ) ?? null;

const statusCustomList = (
    profile: EntityProfile,
    context: BlueprintContext,
): CustomList | null => {
    switch (profile.statusListKey) {
        case "client":
            return context.clientStatusCustomList;
        case "homeCareLead":
            return context.homeCareLeadStatusCustomList;
        case null:
            return null;
        default: {
            const exhaustiveCheck: never = profile.statusListKey;
            throw new Error(`Unhandled status list key: ${exhaustiveCheck}`);
        }
    }
};

const activeStatusConditions = (
    profile: EntityProfile,
    context: BlueprintContext,
): AdvancedSearchRequestCondition[] | null => {
    const customList = statusCustomList(profile, context);
    if (!customList) {
        return null;
    }
    const categoryIds = profile.activeCategoryKeys.map(
        key => customList.categories.find(category => category.key === key)?.id ?? null,
    );
    if (categoryIds.some(id => !id)) {
        return null;
    }
    return customList.items
        .filter(
            item => !item.isArchived && categoryIds.includes(item.customListCategoryId),
        )
        .map(item => statusCondition(item.id));
};

const statusCondition = (statusId: string): AdvancedSearchRequestCondition => ({
    nodeType: AdvancedSearchConditionNodeType.FieldCondition,
    fieldName: "statusListItem",
    operator: FieldConditionOperator.CustomListEquals,
    textValue: statusId,
});

const scheduleTrigger = (
    statusConditions: AdvancedSearchRequestCondition[],
): WorkflowTriggerInput => ({
    eventType: WorkflowEventType.Schedule,
    schedule: {
        cron: INACTIVITY_CRON,
        entityCondition: {
            nodeType: AdvancedSearchConditionNodeType.And,
            children: [
                {
                    nodeType: AdvancedSearchConditionNodeType.Or,
                    children: statusConditions,
                },
                {
                    nodeType: AdvancedSearchConditionNodeType.FieldCondition,
                    fieldName: "updatedAt",
                    operator: FieldConditionOperator.DateOnOrBefore,
                    relativeDate: {
                        interval: AdvancedSearchRelativeDateInterval.Day,
                        value: -INACTIVITY_DAYS,
                    },
                },
            ],
        },
    },
});

const TRIGGER_SPECS: Record<WorkflowTriggerKind, TriggerSpec> = {
    statusChange: {
        title: "Status changes",
        describe: profile => `When a ${profile.noun}'s status changes to one you choose`,
        stopReason: profile =>
            `The ${profile.noun}'s status changes again before the wait elapses`,
        automatedNotificationLabel: null,
        dueDateDaysDefault: STATUS_CHANGE_DUE_DATE_DAYS,
        taskDescription: profile =>
            `Follow up with ${profile.nameToken} after their status change`,
        recipients: profile => profile.defaultRecipients,
        smsRecipients: profile => profile.defaultSmsRecipients,
        audiences: ["recipients"],
        timing: { mode: "wait", wait: defaultWaitForAction },
        statusFilter: "matcher",
        build: profile =>
            entityUpdatedTrigger([statusMatcher(profile.statusFieldName, [])]),
        matches: triggerInput =>
            triggerInput.eventType === WorkflowEventType.EntityUpdated &&
            !!findStatusMatcher(triggerInput),
    },
    checkLater: {
        title: "Check back later date arrives",
        describe: profile =>
            `When a check back later date is set for a ${profile.noun}, timed around that date`,
        stopReason: () => "The check back later date changes before the wait elapses",
        automatedNotificationLabel: profile =>
            `${capitalize(profile.noun)} check back later`,
        dueDateDaysDefault: DEFAULT_DUE_DATE_DAYS,
        taskDescription: profile => `Check back later follow-up: ${profile.nameToken}`,
        recipients: profile => profile.defaultRecipients,
        smsRecipients: profile => profile.defaultSmsRecipients,
        audiences: ["recipients"],
        timing: {
            mode: "anchored",
            dateField: "statusCheckLaterDate",
            anchorLabel: "the check back later date",
            defaultDays: "1",
        },
        statusFilter: "seeded",
        build: profile =>
            entityUpdatedTrigger([
                fieldMatcher("statusCheckLaterDate", "$NOT_EMPTY"),
                statusMatcher(profile.statusFieldName, ["checkLater"]),
            ]),
        matches: triggerInput => hasEntityUpdate(triggerInput, "statusCheckLaterDate"),
    },
    moveIn: {
        title: "Move-in happens",
        describe: profile =>
            `When a move-in date is set for a ${profile.noun}, timed around that date`,
        stopReason: () => "The move-in date changes before the wait elapses",
        automatedNotificationLabel: profile =>
            `${capitalize(profile.noun)} move-in follow-up`,
        dueDateDaysDefault: DEFAULT_DUE_DATE_DAYS,
        taskDescription: profile => `Move-in follow-up: ${profile.nameToken}`,
        recipients: profile => profile.defaultRecipients,
        smsRecipients: profile => profile.defaultSmsRecipients,
        audiences: ["recipients"],
        timing: {
            mode: "anchored",
            dateField: "moveTimelineActual",
            anchorLabel: "the move-in date",
            defaultDays: "7",
        },
        statusFilter: "seeded",
        build: profile =>
            entityUpdatedTrigger([
                fieldMatcher("moveTimelineActual", "$NOT_EMPTY"),
                statusMatcher(profile.statusFieldName, PLACED_STATUS_KEYS),
            ]),
        matches: triggerInput => hasEntityUpdate(triggerInput, "moveTimelineActual"),
    },
    referralSourceChosen: {
        title: "Referral source is set",
        describe: profile => `When a referral source is chosen for a ${profile.noun}`,
        stopReason: () => "The referral source changes before the wait elapses",
        automatedNotificationLabel: profile =>
            `${capitalize(profile.noun)} referral source follow-up`,
        dueDateDaysDefault: DEFAULT_DUE_DATE_DAYS,
        taskDescription: profile =>
            `Follow up with the referral source for ${profile.nameToken}`,
        recipients: profile => profile.referralRecipients ?? profile.defaultRecipients,
        smsRecipients: profile =>
            profile.referralSmsRecipients ?? profile.defaultSmsRecipients,
        audiences: ["recipients"],
        timing: { mode: "wait", wait: defaultWaitForAction },
        statusFilter: "none",
        build: () =>
            entityUpdatedTrigger([fieldMatcher("referralContactId", "$NOT_EMPTY")]),
        matches: triggerInput => hasEntityUpdate(triggerInput, "referralContactId"),
    },
    communityChosen: {
        title: "Community is chosen",
        describe: profile => `When a community is selected for a ${profile.noun}`,
        stopReason: () => "The chosen community changes before the wait elapses",
        automatedNotificationLabel: profile =>
            `${capitalize(profile.noun)} chosen community follow-up`,
        dueDateDaysDefault: DEFAULT_DUE_DATE_DAYS,
        taskDescription: profile =>
            `Follow up on the chosen community for ${profile.nameToken}`,
        recipients: profile => profile.defaultRecipients,
        smsRecipients: profile => profile.defaultSmsRecipients,
        audiences: ["recipients", "unselectedCommunities"],
        timing: {
            mode: "wait",
            wait: action =>
                action.kind === "email" && action.audience === "unselectedCommunities"
                    ? COMMUNITY_BROADCAST_WAIT_RECIPE
                    : defaultWaitForAction(action),
        },
        statusFilter: "seeded",
        build: profile =>
            entityUpdatedTrigger([
                fieldMatcher("chosenCommunityId", "$NOT_EMPTY"),
                statusMatcher(profile.statusFieldName, PLACED_STATUS_KEYS),
            ]),
        matches: triggerInput => hasEntityUpdate(triggerInput, "chosenCommunityId"),
    },
    emailOpened: {
        title: "Email opened after inactivity",
        describe: profile =>
            `When a ${profile.noun} opens an email after a period of no activity`,
        stopReason: () => null,
        automatedNotificationLabel: profile =>
            `${capitalize(profile.noun)} re-engagement`,
        dueDateDaysDefault: DEFAULT_DUE_DATE_DAYS,
        taskDescription: profile =>
            `Follow up with re-engaged ${profile.noun} ${profile.nameToken}`,
        recipients: profile => profile.defaultRecipients,
        smsRecipients: profile => profile.defaultSmsRecipients,
        audiences: ["recipients"],
        timing: { mode: "none", recipe: EMAIL_OPENED_WAIT_RECIPE },
        statusFilter: "emailOpened",
        build: () => ({
            eventType: WorkflowEventType.EmailOpened,
            emailOpened: {
                entityFieldMatchers: [],
                activityThresholdDays: EMAIL_OPENED_THRESHOLD_DAYS,
            },
        }),
        matches: triggerInput =>
            triggerInput.eventType === WorkflowEventType.EmailOpened &&
            !!triggerInput.emailOpened,
    },
    inactivity: {
        title: "No recent activity",
        describe: profile =>
            `Checks daily for active ${profile.noun}s with no recent activity`,
        stopReason: () => null,
        automatedNotificationLabel: profile => `Inactive ${profile.noun} follow-up`,
        dueDateDaysDefault: DEFAULT_DUE_DATE_DAYS,
        taskDescription: profile =>
            `Follow up with inactive ${profile.noun} ${profile.nameToken}`,
        recipients: profile => profile.defaultRecipients,
        smsRecipients: profile => profile.defaultSmsRecipients,
        audiences: ["recipients"],
        timing: { mode: "none", recipe: null },
        statusFilter: "schedule",
        build: (profile, context) => {
            const conditions = activeStatusConditions(profile, context);
            return conditions ? scheduleTrigger(conditions) : null;
        },
        matches: triggerInput =>
            triggerInput.eventType === WorkflowEventType.Schedule &&
            !!findScheduleStatusCondition(triggerInput) &&
            !!findScheduleDateCondition(triggerInput),
    },
    careStart: {
        title: "Care starts",
        describe: profile =>
            `When a care start date is set for a ${profile.noun}, timed around that date`,
        stopReason: () => "The care start date changes before the wait elapses",
        automatedNotificationLabel: profile =>
            `${capitalize(profile.noun)} care start follow-up`,
        dueDateDaysDefault: DEFAULT_DUE_DATE_DAYS,
        taskDescription: profile => `Care start follow-up: ${profile.nameToken}`,
        recipients: profile => profile.referralRecipients ?? profile.defaultRecipients,
        smsRecipients: profile =>
            profile.referralSmsRecipients ?? profile.defaultSmsRecipients,
        audiences: ["recipients"],
        timing: {
            mode: "anchored",
            dateField: "actualStartDate",
            anchorLabel: "the care start date",
            defaultDays: "7",
        },
        statusFilter: "seeded",
        build: profile =>
            entityUpdatedTrigger([
                fieldMatcher("actualStartDate", "$NOT_EMPTY"),
                statusMatcher(profile.statusFieldName, CARE_START_STATUS_KEYS),
            ]),
        matches: triggerInput => hasEntityUpdate(triggerInput, "actualStartDate"),
    },
    addedToOrganization: {
        title: "Added to an organization",
        describe: profile =>
            `When a ${profile.noun} is first attached to an organization`,
        stopReason: profile =>
            `The ${profile.noun}'s organization changes before the wait elapses`,
        automatedNotificationLabel: profile =>
            `${capitalize(profile.noun)} new organization`,
        dueDateDaysDefault: DEFAULT_DUE_DATE_DAYS,
        taskDescription: profile => `Introduce yourself to ${profile.nameToken}`,
        recipients: profile => profile.defaultRecipients,
        smsRecipients: profile => profile.defaultSmsRecipients,
        audiences: ["recipients"],
        timing: { mode: "wait", wait: defaultWaitForAction },
        statusFilter: "none",
        build: () =>
            entityUpdatedTrigger([
                fieldMatcher("businessId", "$NOT_EMPTY"),
                { fieldName: "businessId", previous: { specialValue: "$EMPTY" } },
            ]),
        matches: triggerInput => hasEntityUpdate(triggerInput, "businessId"),
    },
};

const TRIGGER_DETECTION_ORDER: WorkflowTriggerKind[] = [
    "careStart",
    "moveIn",
    "checkLater",
    "addedToOrganization",
    "communityChosen",
    "referralSourceChosen",
    "statusChange",
    "emailOpened",
    "inactivity",
];

const profileFor = (entityType: AdvancedSearchEntityType): EntityProfile | null =>
    ENTITY_PROFILES[entityType] ?? null;

const STATUS_MATCHER_FIELD_NAMES = ["status", "statusListItem"];

const DETECTION_CONTEXT: BlueprintContext = {
    clientStatusCustomList: null,
    homeCareLeadStatusCustomList: null,
    newActionId: generateActionId,
};

const modelledMatcherFieldNames = (): Set<string> => {
    const names = new Set<string>(STATUS_MATCHER_FIELD_NAMES);
    for (const entityType of Object.keys(ENTITY_PROFILES)) {
        const profile = profileFor(entityType as AdvancedSearchEntityType);
        if (!profile) {
            continue;
        }
        for (const kind of profile.triggerKinds) {
            const built = TRIGGER_SPECS[kind].build(profile, DETECTION_CONTEXT);
            for (const matcher of built?.entityUpdated?.fieldMatchers ?? []) {
                names.add(matcher.fieldName);
            }
        }
    }
    return names;
};

const MODELLED_MATCHER_FIELD_NAMES = modelledMatcherFieldNames();

const hasUnmodelledMatcher = (triggerInput: WorkflowTriggerInput): boolean =>
    (triggerInput.entityUpdated?.fieldMatchers ?? []).some(
        matcher => !MODELLED_MATCHER_FIELD_NAMES.has(matcher.fieldName),
    );

const audiencesFor = (
    entityType: AdvancedSearchEntityType,
    triggerKind: WorkflowTriggerKind,
): EmailAudience[] => {
    const profile = profileFor(entityType);
    if (!profile || !profile.triggerKinds.includes(triggerKind)) {
        return ["recipients"];
    }
    return TRIGGER_SPECS[triggerKind].audiences;
};

const normalizeAction = (
    action: WorkflowAction,
    entityType: AdvancedSearchEntityType,
    triggerKind: WorkflowTriggerKind,
): WorkflowAction => {
    if (action.kind !== "email") {
        return action;
    }
    return audiencesFor(entityType, triggerKind).includes(action.audience)
        ? action
        : { kind: "email", audience: "recipients" };
};

export const actionKey = (action: WorkflowAction): string =>
    action.kind === "email" && action.audience !== "recipients"
        ? `email:${action.audience}`
        : action.kind;

export const actionsKey = (actions: readonly BlueprintAction[]): string =>
    actions.map(entry => actionKey(entry.action)).join("+");

const sameActions = (
    a: readonly BlueprintAction[],
    b: readonly BlueprintAction[],
): boolean =>
    a.length === b.length &&
    a.every(
        (entry, index) =>
            entry.id === b[index].id &&
            actionKey(entry.action) === actionKey(b[index].action),
    );

export const sameBlueprint = (a: WorkflowBlueprint, b: WorkflowBlueprint): boolean =>
    a.entityType === b.entityType &&
    a.triggerKind === b.triggerKind &&
    sameActions(a.actions, b.actions);

const normalizeActions = (
    actions: readonly BlueprintAction[],
    entityType: AdvancedSearchEntityType,
    triggerKind: WorkflowTriggerKind,
): BlueprintAction[] =>
    actions.map(entry => ({
        id: entry.id,
        action: normalizeAction(entry.action, entityType, triggerKind),
    }));

export const blueprintEntityTypes = (
    entityTypes: readonly AdvancedSearchEntityType[],
): AdvancedSearchEntityType[] =>
    entityTypes.filter(entityType => !!profileFor(entityType));

const triggerOption = (
    profile: EntityProfile,
    kind: WorkflowTriggerKind,
): TriggerOption => ({
    kind,
    title: TRIGGER_SPECS[kind].title,
    description: TRIGGER_SPECS[kind].describe(profile),
});

export const availableTriggers = (
    entityType: AdvancedSearchEntityType,
    context: BlueprintContext,
): TriggerOption[] => {
    const profile = profileFor(entityType);
    if (!profile) {
        return [];
    }
    return profile.triggerKinds
        .filter(kind => !!TRIGGER_SPECS[kind].build(profile, context))
        .map(kind => triggerOption(profile, kind));
};

export const availableTriggersIncluding = (
    entityType: AdvancedSearchEntityType,
    context: BlueprintContext,
    currentKind: WorkflowTriggerKind,
): TriggerOption[] => {
    const profile = profileFor(entityType);
    const available = availableTriggers(entityType, context);
    if (
        !profile ||
        !profile.triggerKinds.includes(currentKind) ||
        available.some(option => option.kind === currentKind)
    ) {
        return available;
    }
    return [...available, triggerOption(profile, currentKind)];
};

export const actionOptions = (blueprint: WorkflowBlueprint): ActionOption[] => {
    const audiences = audiencesFor(blueprint.entityType, blueprint.triggerKind);
    const emailOptions: ActionOption[] = audiences.map(audience =>
        audience === "unselectedCommunities"
            ? {
                  key: "email:unselectedCommunities",
                  action: { kind: "email", audience },
                  title: "Email unselected communities",
                  description: "Email every community that was not chosen",
              }
            : {
                  key: "email",
                  action: { kind: "email", audience },
                  title: "Email",
                  description: "Send an automated email",
              },
    );
    return [
        ...emailOptions,
        {
            key: "sms",
            action: { kind: "sms" },
            title: "Text message",
            description: "Send an automated text message (SMS)",
        },
        {
            key: "task",
            action: { kind: "task" },
            title: "Task",
            description: "Create a task for your team",
        },
    ];
};

export const triggerStopReason = (blueprint: WorkflowBlueprint): string | null => {
    const profile = profileFor(blueprint.entityType);
    if (!profile) {
        return null;
    }
    return TRIGGER_SPECS[blueprint.triggerKind].stopReason(profile);
};

export const triggerOwnsStatusGate = (blueprint: WorkflowBlueprint): boolean =>
    TRIGGER_SPECS[blueprint.triggerKind].statusFilter === "seeded";

const STATUS_GATED_FILTER_MODES: StatusFilterMode[] = ["seeded", "matcher"];

export const statusGateNeverMatches = (
    blueprint: WorkflowBlueprint,
    statusIds: readonly string[],
): boolean =>
    STATUS_GATED_FILTER_MODES.includes(
        TRIGGER_SPECS[blueprint.triggerKind].statusFilter,
    ) && statusIds.length === 0;

export const withEntityType = (
    blueprint: WorkflowBlueprint,
    entityType: AdvancedSearchEntityType,
): WorkflowBlueprint | null => {
    const profile = profileFor(entityType);
    if (!profile) {
        return null;
    }
    const triggerKind = profile.triggerKinds.includes(blueprint.triggerKind)
        ? blueprint.triggerKind
        : profile.triggerKinds[0];
    return {
        entityType,
        triggerKind,
        actions: normalizeActions(blueprint.actions, entityType, triggerKind),
    };
};

export const withTrigger = (
    blueprint: WorkflowBlueprint,
    triggerKind: WorkflowTriggerKind,
): WorkflowBlueprint | null => {
    const profile = profileFor(blueprint.entityType);
    if (!profile || !profile.triggerKinds.includes(triggerKind)) {
        return null;
    }
    return {
        entityType: blueprint.entityType,
        triggerKind,
        actions: normalizeActions(blueprint.actions, blueprint.entityType, triggerKind),
    };
};

const withActions = (
    blueprint: WorkflowBlueprint,
    actions: BlueprintAction[],
): WorkflowBlueprint => ({
    entityType: blueprint.entityType,
    triggerKind: blueprint.triggerKind,
    actions,
});

export const withActionKind = (
    blueprint: WorkflowBlueprint,
    actionId: WorkflowActionId,
    action: WorkflowAction,
): WorkflowBlueprint =>
    withActions(
        blueprint,
        blueprint.actions.map(entry =>
            entry.id === actionId
                ? {
                      id: entry.id,
                      action: normalizeAction(
                          action,
                          blueprint.entityType,
                          blueprint.triggerKind,
                      ),
                  }
                : entry,
        ),
    );

export const withActionAppended = (
    blueprint: WorkflowBlueprint,
    action: WorkflowAction,
    id: WorkflowActionId,
): WorkflowBlueprint =>
    withActions(blueprint, [
        ...blueprint.actions,
        {
            id,
            action: normalizeAction(action, blueprint.entityType, blueprint.triggerKind),
        },
    ]);

export const withActionRemoved = (
    blueprint: WorkflowBlueprint,
    actionId: WorkflowActionId,
): WorkflowBlueprint =>
    blueprint.actions.length <= 1
        ? blueprint
        : withActions(
              blueprint,
              blueprint.actions.filter(entry => entry.id !== actionId),
          );

export const withActionMoved = (
    blueprint: WorkflowBlueprint,
    actionId: WorkflowActionId,
    direction: ActionMoveDirection,
): WorkflowBlueprint => {
    const index = blueprint.actions.findIndex(entry => entry.id === actionId);
    const target = direction === "up" ? index - 1 : index + 1;
    if (index === -1 || target < 0 || target >= blueprint.actions.length) {
        return blueprint;
    }
    const actions = [...blueprint.actions];
    actions[index] = blueprint.actions[target];
    actions[target] = blueprint.actions[index];
    return withActions(blueprint, actions);
};

const detectAction = (action: WorkflowActionEntityInput): WorkflowAction | null => {
    if (action.task) {
        return { kind: "task" };
    }
    if (action.sms) {
        return { kind: "sms" };
    }
    if (action.email) {
        return {
            kind: "email",
            audience:
                action.email.discreteTo === "referredUnselectedCommunities"
                    ? "unselectedCommunities"
                    : "recipients",
        };
    }
    return null;
};

const detectActions = (payload: WorkflowPayload): BlueprintAction[] | null => {
    const entities = payload.actionEntities ?? [];
    if (entities.length === 0) {
        return null;
    }
    const actions: BlueprintAction[] = [];
    for (const entity of entities) {
        const id = actionEntityId(entity);
        const action = detectAction(entity);
        if (!id || !action) {
            return null;
        }
        actions.push({ id, action });
    }
    return actions;
};

const detectTriggerKind = (
    triggerInput: WorkflowTriggerInput,
    profile: EntityProfile,
): WorkflowTriggerKind | null => {
    if (hasUnmodelledMatcher(triggerInput)) {
        return null;
    }
    return (
        TRIGGER_DETECTION_ORDER.find(
            kind =>
                profile.triggerKinds.includes(kind) &&
                TRIGGER_SPECS[kind].matches(triggerInput),
        ) ?? null
    );
};

export const detectBlueprint = (payload: WorkflowPayload): WorkflowBlueprint | null => {
    const profile = profileFor(payload.attachTo);
    if (!profile) {
        return null;
    }
    const triggerKind = detectTriggerKind(payload.triggerInput, profile);
    const actions = detectActions(payload);
    if (!triggerKind || !actions) {
        return null;
    }
    return {
        entityType: payload.attachTo,
        triggerKind,
        actions: normalizeActions(actions, payload.attachTo, triggerKind),
    };
};

const readStatuses = (
    triggerInput: WorkflowTriggerInput,
    triggerKind: WorkflowTriggerKind,
): string[] => {
    switch (TRIGGER_SPECS[triggerKind].statusFilter) {
        case "matcher":
            return findStatusMatcher(triggerInput)?.current?.values ?? [];
        case "emailOpened":
            return findEmailOpenedStatusMatcher(triggerInput)?.current?.values ?? [];
        case "schedule":
            return (findScheduleStatusCondition(triggerInput)?.children ?? []).map(
                child => child.textValue,
            );
        case "seeded":
        case "none":
            return [];
        default: {
            const exhaustiveCheck: never = TRIGGER_SPECS[triggerKind].statusFilter;
            throw new Error(`Unhandled status filter mode: ${exhaustiveCheck}`);
        }
    }
};

const applyStatuses = (
    triggerInput: WorkflowTriggerInput,
    triggerKind: WorkflowTriggerKind,
    statusIds: string[],
): void => {
    switch (TRIGGER_SPECS[triggerKind].statusFilter) {
        case "matcher": {
            const matcher = findStatusMatcher(triggerInput);
            if (matcher) {
                matcher.fieldName = "statusListItem";
                matcher.current = { values: statusIds };
            }
            return;
        }
        case "emailOpened": {
            if (triggerInput.emailOpened) {
                triggerInput.emailOpened.entityFieldMatchers = [
                    ...triggerInput.emailOpened.entityFieldMatchers,
                    { fieldName: "statusListItem", current: { values: statusIds } },
                ];
            }
            return;
        }
        case "schedule": {
            const condition = findScheduleStatusCondition(triggerInput);
            if (condition) {
                condition.children = statusIds.map(statusId => statusCondition(statusId));
            }
            return;
        }
        case "seeded":
        case "none":
            return;
        default: {
            const exhaustiveCheck: never = TRIGGER_SPECS[triggerKind].statusFilter;
            throw new Error(`Unhandled status filter mode: ${exhaustiveCheck}`);
        }
    }
};

const composeTrigger = (
    base: WorkflowPayload,
    previous: WorkflowBlueprint | null,
    target: WorkflowBlueprint,
    built: WorkflowTriggerInput,
): WorkflowTriggerInput => {
    if (!previous || previous.entityType !== target.entityType) {
        return built;
    }
    if (previous.triggerKind === target.triggerKind) {
        return base.triggerInput;
    }
    const statusIds = readStatuses(base.triggerInput, previous.triggerKind);
    if (statusIds.length > 0) {
        applyStatuses(built, target.triggerKind, statusIds);
    }
    return built;
};

const actionVerb = (action: WorkflowAction): string => {
    switch (action.kind) {
        case "task":
            return "create the task";
        case "sms":
            return "send the text message";
        case "email":
            return "send the email";
    }
};

const timingSettings = (
    trigger: TriggerSpec,
    action: WorkflowAction,
): WorkflowPayloadSettingsInput[] => {
    if (trigger.timing.mode !== "anchored") {
        return [];
    }
    return [
        {
            scope: "tenant",
            key: "delay",
            type: WorkflowSettingType.Number,
            label: `Days after ${trigger.timing.anchorLabel} to ${actionVerb(action)}`,
            description: "",
            defaultValue: trigger.timing.defaultDays,
        },
    ];
};

const taskSettings = (
    profile: EntityProfile,
    trigger: TriggerSpec,
    suffix: string,
): WorkflowPayloadSettingsInput[] => [
    {
        scope: "tenant",
        key: actionSettingKey("description", suffix),
        type: WorkflowSettingType.String,
        label: "Task description",
        description: "",
        defaultValue: trigger.taskDescription(profile),
    },
    {
        scope: "tenant",
        key: actionSettingKey("dueDateDays", suffix),
        type: WorkflowSettingType.Number,
        label: "Task due date offset (days after the workflow runs)",
        description: "",
        defaultValue: trigger.dueDateDaysDefault,
    },
    {
        scope: "tenant",
        key: actionSettingKey("assignedTo", suffix),
        type: WorkflowSettingType.User,
        label: `Assign task to (leave blank to use the ${profile.noun}'s assigned user)`,
        description: "",
        defaultValue: "",
    },
];

const emailTemplateSettings = (suffix: string): WorkflowPayloadSettingsInput[] => [
    {
        scope: "tenant",
        key: actionSettingKey("emailTemplate", suffix),
        type: WorkflowSettingType.EmailTemplateId,
        label: "",
        description: "",
        defaultValue: "",
    },
    {
        scope: "tenant",
        key: actionSettingKey("bccMyself", suffix),
        type: WorkflowSettingType.Boolean,
        label: "BCC sender on email",
        description: "The email sender will receive a copy of the email",
        defaultValue: "false",
    },
];

const emailSettings = (
    profile: EntityProfile,
    trigger: TriggerSpec,
    audience: EmailAudience,
    suffix: string,
): WorkflowPayloadSettingsInput[] => {
    if (audience === "unselectedCommunities") {
        return emailTemplateSettings(suffix);
    }
    return [
        {
            scope: "tenant",
            key: actionSettingKey("recipients", suffix),
            type: WorkflowSettingType.ClientEmails,
            label: "Choose recipients",
            description: "",
            defaultValue: trigger.recipients(profile),
        },
        ...emailTemplateSettings(suffix),
    ];
};

const smsSettings = (
    profile: EntityProfile,
    trigger: TriggerSpec,
    suffix: string,
): WorkflowPayloadSettingsInput[] => [
    {
        scope: "tenant",
        key: actionSettingKey("smsRecipients", suffix),
        type: WorkflowSettingType.ClientPhones,
        label: "Choose recipients",
        description: "",
        defaultValue: trigger.smsRecipients(profile),
    },
    {
        scope: "tenant",
        key: actionSettingKey("smsBody", suffix),
        type: WorkflowSettingType.SmsBody,
        label: "Message",
        description: "",
        defaultValue: "",
    },
];

const actionSettings = (
    profile: EntityProfile,
    trigger: TriggerSpec,
    action: WorkflowAction,
    suffix: string,
): WorkflowPayloadSettingsInput[] => {
    switch (action.kind) {
        case "task":
            return taskSettings(profile, trigger, suffix);
        case "sms":
            return smsSettings(profile, trigger, suffix);
        case "email":
            return emailSettings(profile, trigger, action.audience, suffix);
    }
};

interface ComposedAction extends BlueprintAction {
    suffix: string;
}

const composeSettings = (
    profile: EntityProfile,
    trigger: TriggerSpec,
    actions: readonly ComposedAction[],
): WorkflowPayloadSettingsInput[] => [
    ...timingSettings(trigger, actions[0].action),
    ...actions.flatMap(entry =>
        actionSettings(profile, trigger, entry.action, entry.suffix),
    ),
];

const notificationSubject = (
    trigger: TriggerSpec,
    profile: EntityProfile,
): string | null =>
    trigger.automatedNotificationLabel
        ? `${trigger.automatedNotificationLabel(profile)} task assigned to you`
        : null;

const isComposedSubject = (subject: string, profile: EntityProfile): boolean =>
    TRIGGER_DETECTION_ORDER.some(
        kind => notificationSubject(TRIGGER_SPECS[kind], profile) === subject,
    );

const baseActionById = (
    base: WorkflowPayload,
    actionId: WorkflowActionId,
): WorkflowActionEntityInput | null =>
    (base.actionEntities ?? []).find(action => actionEntityId(action) === actionId) ??
    null;

const settingToken = (base: string, suffix: string): string =>
    `{{settings.${actionSettingKey(base, suffix)}}}`;

const composeTaskAction = (
    trigger: TriggerSpec,
    profile: EntityProfile,
    baseTask: WorkflowActionTaskEntityInput | null,
    id: WorkflowActionId,
    suffix: string,
): WorkflowActionTaskEntityInput => {
    const composedSubject = notificationSubject(trigger, profile);
    const keepsOwnSubject =
        !!baseTask?.notification?.subject &&
        !isComposedSubject(baseTask.notification.subject, profile);
    const composed: WorkflowActionTaskEntityInput = {
        id,
        text: settingToken("description", suffix),
        dueDate: `+${settingToken("dueDateDays", suffix)}d`,
        assignedTo: settingToken("assignedTo", suffix),
        taskTypeId: baseTask?.taskTypeId ?? null,
        notifyOwner: baseTask ? !!baseTask.notifyOwner : !!composedSubject,
        notification: keepsOwnSubject
            ? baseTask.notification
            : composedSubject
              ? { subject: composedSubject }
              : null,
    };
    if (!baseTask) {
        return composed;
    }
    return {
        ...composed,
        recurrencePattern: baseTask.recurrencePattern,
        recurrenceInterval: baseTask.recurrenceInterval,
        recurrenceDaysOfWeek: baseTask.recurrenceDaysOfWeek,
        recurrenceDayOfMonth: baseTask.recurrenceDayOfMonth,
    };
};

const composeSmsAction = (
    baseSms: WorkflowActionSmsEntityInput | null,
    id: WorkflowActionId,
    suffix: string,
): WorkflowActionSmsEntityInput => ({
    id,
    to: settingToken("smsRecipients", suffix),
    body: settingToken("smsBody", suffix),
    fromPhoneLineId: baseSms?.fromPhoneLineId ?? null,
    consentAcknowledged: !!baseSms?.consentAcknowledged,
});

const composeEmailAction = (
    audience: EmailAudience,
    id: WorkflowActionId,
    suffix: string,
): WorkflowActionEmailEntityInput => {
    const template = {
        id,
        subject: settingToken("emailTemplate", suffix),
        body: settingToken("emailTemplate", suffix),
        attachments: settingToken("emailTemplate", suffix),
        bccMyself: settingToken("bccMyself", suffix),
    };
    if (audience === "unselectedCommunities") {
        return { to: "", discreteTo: "referredUnselectedCommunities", ...template };
    }
    return { to: settingToken("recipients", suffix), ...template };
};

const composeAction = (
    trigger: TriggerSpec,
    profile: EntityProfile,
    entry: ComposedAction,
    baseAction: WorkflowActionEntityInput | null,
): WorkflowActionEntityInput => {
    switch (entry.action.kind) {
        case "task":
            return {
                task: composeTaskAction(
                    trigger,
                    profile,
                    baseAction?.task ?? null,
                    entry.id,
                    entry.suffix,
                ),
            };
        case "sms":
            return {
                sms: composeSmsAction(baseAction?.sms ?? null, entry.id, entry.suffix),
            };
        case "email":
            return {
                email: composeEmailAction(entry.action.audience, entry.id, entry.suffix),
            };
    }
};

const intervalOfAction = (action: WorkflowActionEntityInput | null): string | null => {
    const recipe = action ? actionEntityDelayRecipe(action) : null;
    return isActionIntervalRecipe(recipe) ? recipe : null;
};

const waitOfDisplacedAction = (base: WorkflowPayload, position: number): string | null =>
    intervalOfAction(base.actionEntities?.[position] ?? null);

const actionWait = (
    base: WorkflowPayload,
    actionId: WorkflowActionId,
    position: number,
): string =>
    intervalOfAction(baseActionById(base, actionId)) ??
    waitOfDisplacedAction(base, position) ??
    DEFAULT_ACTION_INTERVAL_RECIPE;

const composeActions = (
    trigger: TriggerSpec,
    profile: EntityProfile,
    actions: readonly ComposedAction[],
    base: WorkflowPayload,
): WorkflowActionEntityInput[] =>
    actions.map((entry, position) => {
        const action = composeAction(
            trigger,
            profile,
            entry,
            baseActionById(base, entry.id),
        );
        return position === 0
            ? action
            : withActionEntityDelayRecipe(action, actionWait(base, entry.id, position));
    });

const actionSuffixes = (
    actions: readonly BlueprintAction[],
    base: WorkflowPayload,
): string[] => {
    const used = (base.actionEntities ?? []).map(actionEntitySettingSuffix);
    const taken: string[] = [];
    return actions.map(entry => {
        const baseAction = baseActionById(base, entry.id);
        const own = baseAction ? actionEntitySettingSuffix(baseAction) : null;
        const suffix =
            own !== null && !taken.includes(own)
                ? own
                : nextActionSettingSuffix([...used, ...taken]);
        taken.push(suffix);
        return suffix;
    });
};

interface ComposedTiming {
    delay: number;
    delayRecipe: string | null;
}

const keepsExistingWait = (
    base: WorkflowPayload,
    previous: WorkflowBlueprint | null,
): boolean => {
    if (!previous || TRIGGER_SPECS[previous.triggerKind].timing.mode !== "wait") {
        return false;
    }
    const recipe = base.delayRecipe || null;
    return recipe === null || isWorkflowWaitRecipe(recipe);
};

const composeTiming = (
    profile: EntityProfile,
    trigger: TriggerSpec,
    action: WorkflowAction,
    base: WorkflowPayload,
    previous: WorkflowBlueprint | null,
): ComposedTiming => {
    switch (trigger.timing.mode) {
        case "anchored":
            return {
                delay: 0,
                delayRecipe: `{{${profile.tokenRoot}.${trigger.timing.dateField}}}+{{settings.delay}}d`,
            };
        case "none":
            return { delay: 0, delayRecipe: trigger.timing.recipe };
        case "wait":
            return keepsExistingWait(base, previous)
                ? { delay: base.delay, delayRecipe: base.delayRecipe ?? null }
                : { delay: 0, delayRecipe: trigger.timing.wait(action) };
        default: {
            const exhaustiveCheck: never = trigger.timing;
            throw new Error(
                `Unhandled trigger timing: ${JSON.stringify(exhaustiveCheck)}`,
            );
        }
    }
};

const composePayload = (
    base: WorkflowPayload,
    target: WorkflowBlueprint,
    context: BlueprintContext,
): WorkflowPayload | null => {
    const profile = profileFor(target.entityType);
    if (!profile || !profile.triggerKinds.includes(target.triggerKind)) {
        return null;
    }
    const trigger = TRIGGER_SPECS[target.triggerKind];
    const built = trigger.build(profile, context);
    if (!built) {
        return null;
    }
    const actions = normalizeActions(
        target.actions,
        target.entityType,
        target.triggerKind,
    );
    if (actions.length === 0) {
        return null;
    }
    const previous = detectBlueprint(base);
    const timing = composeTiming(profile, trigger, actions[0].action, base, previous);
    const suffixes = actionSuffixes(actions, base);
    const composed: ComposedAction[] = actions.map((entry, index) => ({
        ...entry,
        suffix: suffixes[index],
    }));

    return {
        ...base,
        attachTo: target.entityType,
        triggerInput: composeTrigger(base, previous, target, built),
        settings: composeSettings(profile, trigger, composed),
        actionEntities: composeActions(trigger, profile, composed, base),
        delay: timing.delay,
        delayRecipe: timing.delayRecipe,
    };
};

const nextDefaultWorkflowName = (existingNames: string[]): string => {
    const taken = new Set(existingNames.map(name => name.trim().toLowerCase()));
    if (!taken.has(DEFAULT_WORKFLOW_NAME.toLowerCase())) {
        return DEFAULT_WORKFLOW_NAME;
    }
    let suffix = 2;
    while (taken.has(`${DEFAULT_WORKFLOW_NAME} ${suffix}`.toLowerCase())) {
        suffix++;
    }
    return `${DEFAULT_WORKFLOW_NAME} ${suffix}`;
};

export const composeNewWorkflow = (
    target: WorkflowBlueprint,
    context: BlueprintContext,
    input: NewWorkflowInput,
): WorkflowPayload | null => {
    const seed: WorkflowPayload = {
        name: nextDefaultWorkflowName(input.existingNames),
        description: "",
        settings: [],
        triggerInput: { eventType: WorkflowEventType.EntityUpdated },
        attachTo: target.entityType,
        delay: 0,
        delayRecipe: null,
        actionEntities: [],
        status: WorkflowStatus.Draft,
        defaultActorId: input.currentUserId,
        actorMode: WorkflowActorMode.AssignedUser,
        regionVisibility: { regions: input.regionVisibility },
    };
    return composePayload(seed, target, context);
};

const ENTITY_TOKEN_ROOTS = [
    "client",
    "community",
    "homeCareLead",
    "referenceBusiness",
    "referenceContact",
];

const tokenRoots = (value: string): string[] =>
    value
        .split("{{")
        .slice(1)
        .map(token => token.split(".")[0].trim().toLowerCase());

const referencesAnotherRecordType = (
    value: string,
    entityType: AdvancedSearchEntityType,
): boolean => {
    const root = (profileFor(entityType)?.tokenRoot ?? "").toLowerCase();
    return tokenRoots(value).some(
        candidate =>
            candidate !== root &&
            ENTITY_TOKEN_ROOTS.some(known => known.toLowerCase() === candidate),
    );
};

export const applyBlueprint = (
    current: WorkflowPayload,
    storedSettings: StoredSetting[],
    target: WorkflowBlueprint,
    context: BlueprintContext,
): BlueprintResult | null => {
    const payload = composePayload(current, target, context);
    if (!payload) {
        return null;
    }
    const previous = detectBlueprint(current);
    const settingResets =
        previous && previous.entityType !== target.entityType
            ? payload.settings
                  .filter(setting =>
                      storedSettings.some(
                          stored =>
                              stored.key === setting.key &&
                              referencesAnotherRecordType(
                                  stored.textValue,
                                  target.entityType,
                              ),
                      ),
                  )
                  .map(setting => ({
                      key: setting.key,
                      textValue: setting.defaultValue,
                  }))
            : [];
    return { payload, settingResets };
};

export const blueprintResetNotice = (
    result: BlueprintResult,
    previousEntityType: AdvancedSearchEntityType,
    targetEntityType: AdvancedSearchEntityType,
    mode: CrmMode,
): string | null => {
    if (result.settingResets.length === 0) {
        return null;
    }
    const labels = result.settingResets.map(reset => {
        const setting = result.payload.settings.find(s => s.key === reset.key);
        return setting?.label || reset.key;
    });
    return `Now running on ${workflowEntityTypeLabel(targetEntityType, mode)}. These settings pointed at ${workflowEntityTypeLabel(previousEntityType, mode)} fields and were reset: ${labels.join(", ")}.`;
};

export const defaultBlueprint = (
    entityTypes: readonly AdvancedSearchEntityType[],
    context: BlueprintContext,
): WorkflowBlueprint | null => {
    for (const entityType of blueprintEntityTypes(entityTypes)) {
        const triggers = availableTriggers(entityType, context);
        if (triggers.length === 0) {
            continue;
        }
        return {
            entityType,
            triggerKind: triggers[0].kind,
            actions: [
                {
                    id: context.newActionId(),
                    action: normalizeAction(
                        { kind: "email", audience: "recipients" },
                        entityType,
                        triggers[0].kind,
                    ),
                },
            ],
        };
    }
    return null;
};
